/**
 * retryLabels — which rows get a Retry (U6) and the words for the
 * `resend_messages` operation's outcomes. The operation now delegates every
 * recoverable outbound state to send-now, preserving its existing message row.
 */
import type { OutreachMessageStatus, OutreachOperation, OutreachRow } from '@discovr/types';
import { failedTotal, OPERATION_STALL_COPY } from './operationOutcomes';
import { draftOutcomeLabel } from './wizard/bulkSendCopy';
import { isFirstTouchDraft } from './handoffStates';
import { RECOVERABLE } from './ThreadMessageCard';

export const RETRY_BUSY_TITLE = 'A retry is already running';

export interface RetryTarget {
  messageId: number;
  status: OutreachMessageStatus;
}

export interface RetryTargetOptions {
  /** The owner's ruling (outreach state model finalization): on bulk Retry, a
   *  creator is only sendable once their personalised mail already exists —
   *  the same bar bulk send already holds rows to via the mail-ready icon.
   *  A never-reviewed follow-up or reply draft (awaiting_approval) is that same
   *  "not ready" case (a first-touch draft gets no retry at all: the wizard
   *  sends it), so it is held back exactly like queued/held/unknown_delivery — only failed
   *  (a message that already existed and already went out once) enters the
   *  batch. This is a FRONTEND-ONLY narrowing, additive to Go's
   *  bulkResendSkip (discovr #573), which stays the authority and the TOCTOU
   *  guard: a row that is failed when the operator selects it can be queued
   *  by the time the worker reaches it, so the backend check cannot be
   *  dropped in favour of this one. */
  bulk?: boolean;
}

/** A recovery action is always available for the latest outbound message in a
 * non-terminal state. The API returns the precise blocker when nothing can
 * move yet, such as a missing mailbox or the daily cap. */
export function retryTarget(row: OutreachRow, options: RetryTargetOptions = {}): RetryTarget | null {
  const last = row.lastMessage;
  const status = row.lastOutboundStatus ?? last?.status;
  const messageId = row.lastOutboundMessageId ?? last?.id;
  if (!status || messageId == null) return null;
  if (last?.id !== messageId) return null;
  if (!RECOVERABLE.includes(status)) return null;
  if (isFirstTouchDraft(last.kind, status)) return null;
  if (options.bulk && status !== 'failed') return null;
  return { messageId, status };
}

/** Why a row isn't eligible for bulk Retry, for the bulk bar's own pre-click
 *  split (the owner's ruling: state the split before the click, not only in
 *  the outcome summary after). null means the row has nothing to recover at
 *  all (retryTarget with no options also returns null) -- distinct from
 *  "recoverable, but not in bulk", which this names. */
export function bulkRetryHeldBackReason(row: OutreachRow): string | null {
  if (!retryTarget(row)) return null;
  if (retryTarget(row, { bulk: true })) return null;
  const status = row.lastOutboundStatus ?? row.lastMessage?.status;
  return status === 'unknown_delivery' ? 'needs_manual_check' : 'needs_single_row_route';
}

/** Short caption for the bulk bar stating the split BEFORE the click (the
 *  owner's ruling: a creator is only sendable in bulk once their
 *  personalised mail already exists, same bar bulk send holds rows to —
 *  state that split up front, not only in the outcome summary afterward).
 *  undefined when every retry-eligible selected row is in the batch, or none
 *  of the selection has anything to recover at all. */
export function retryHeldBackNote(rows: readonly OutreachRow[]): string | undefined {
  const heldBack = rows.map(bulkRetryHeldBackReason).filter((reason): reason is string => reason !== null);
  const eligible = rows.filter((row) => retryTarget(row) !== null).length;
  if (heldBack.length === 0 || heldBack.length === eligible) return undefined;
  const labels = [...new Set(heldBack)].map((reason) => retrySkipCopy(reason).toLowerCase());
  return `${heldBack.length} of ${eligible} can't be retried in bulk yet — ${labels.join(', ')}`;
}

export const RETRY_SKIP_LABELS: Record<string, string> = {
  not_resendable: 'Already sent or still queued',
  not_in_campaign: 'Not in this campaign',
  needs_manual_check: 'Check the sent folder first — may already have been delivered', // copy: ours
  // Go's bulkResendSkip (review CRITICAL): held/queued and follow-up/reply
  // awaiting_approval route to the single-row send-now instead of the batch, so a direct link
  // to it (rather than a generic restatement of the status) is the useful copy.
  needs_single_row_route: 'Open this message to send it one at a time',
  // Go's SkipReason (review HIGH-3): these are SendNow's own refusal codes,
  // reachable in bulk only via the TOCTOU window on an already-failed row.
  not_recoverable: 'Changed before it could be recovered',
  daily_cap_reached: "Today's mailbox limit is reached",
  cooldown_override_required: 'Held for a recent contact — open it to send anyway',
  no_mailbox: 'No active mailbox is available',
  address_guard_override_required: 'Held for a risky address — open it to send anyway',
  followup_daily_cap_reached: "This campaign's follow-up limit for today is reached",
};

// The stall close writes operation_stalled; the draft sweeper writes stalled.
const STALL_REASONS = new Set(['operation_stalled', 'stalled']);

/** Only the stall is worded; any other failure reason is a raw Go error and is never rendered. */
export function retryFailureCopy(reason: string): string | null {
  return STALL_REASONS.has(reason) ? OPERATION_STALL_COPY : null;
}

/** The resend's own two skips, then QueueInitial's vocabulary (incl. already_drafted). */
export function retrySkipCopy(reason: string): string {
  return RETRY_SKIP_LABELS[reason] ?? draftOutcomeLabel(reason);
}

/** Nothing retried is an error; any failure is a warning, never a success. */
export function retryToastKind(operation: OutreachOperation): 'success' | 'warning' | 'error' {
  if (operation.appliedCount === 0) return 'error';
  return failedTotal(operation) > 0 ? 'warning' : 'success';
}

/** "Retried 2 · skipped 1 (Already sent or still queued 1)" — the toast when the operation finishes. */
export function retrySummary(operation: OutreachOperation): string {
  const parts = [`Retried ${operation.appliedCount}`];
  if (operation.skippedCount > 0) {
    const counts = new Map<string, number>();
    for (const o of operation.outcomes ?? []) if (o.outcome === 'skipped') counts.set(o.reason ?? 'unknown', (counts.get(o.reason ?? 'unknown') ?? 0) + 1);
    const detail = [...counts].map(([reason, n]) => `${retrySkipCopy(reason)} ${n}`).join(', ');
    parts.push(`skipped ${operation.skippedCount}${detail ? ` (${detail})` : ''}`);
  }
  const failed = failedTotal(operation);
  if (failed > 0) {
    const stalls = (operation.outcomes ?? []).filter((o) => o.outcome === 'failed' && retryFailureCopy(o.reason ?? '')).length;
    parts.push(`failed ${failed}${stalls > 0 ? ` (${OPERATION_STALL_COPY} ${stalls})` : ''}`);
  }
  return parts.join(' · ');
}
